import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';

import { Button, Text } from '@hh.ru/magritte-ui';
import { HhProSolidSize16, HhProSolidSize24 } from '@hh.ru/magritte-ui/service';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import ContainerForMicroFrontend from 'src/components/CodeInjector/ContainerForMicroFrontend';
import { useResumesCount } from 'src/components/Employer/EmployerView/Header/hooks/useResumesCount';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { setResumesTargetEmployerCount } from 'src/models/employer/employerInfo';

import styles from './styles.less';

const HHPRO_RTE_LOAD_EVENT = 'HHPRO_RTE_LOAD_EVENT';
const HHPRO_RTE_CLICK_EVENT = 'ON_HHPRO_RTE_CLICK';
const HHPRO_RTE_RESUMES_COUNT = 'HHPRO_RTE_RESUMES_COUNT';

export const MAX_RESUMES = 3;

const TrlKeys = {
    already: 'employerView.header.wantWorkHere.already',
    wantWorkHere: 'employerView.actions.wantWorkHere.buttonModal',
};

const WantWorkHereManagementButton: TranslatedComponent<{
    stretched?: boolean;
    isMenuItem?: boolean;
    showCounter?: boolean;
    showIcon?: boolean;
}> = ({ trls, stretched, isMenuItem, showCounter, showIcon = true }) => {
    const [microfrontState, setMicrofrontState] = useState<'dirty' | 'fetching' | 'loaded'>('dirty');
    const employer = useSelector((state) => state.employerInfo);
    const logoUrl = useSelector((state) => state.employerLogo.logoUrl);
    const { countResumes } = useResumesCount();
    const already = countResumes > 0;

    const dispatch = useDispatch();

    const dispatchClick = useCallback(() => {
        window.dispatchEvent(
            new CustomEvent(HHPRO_RTE_CLICK_EVENT, {
                detail: {
                    employerId: employer.id,
                    employerName: employer.name,
                    employerLogo: logoUrl,
                },
            })
        );
    }, [employer.id, employer.name, logoUrl]);

    const handleButtonClick = useCallback(() => {
        if (microfrontState === 'loaded') {
            dispatchClick();
            return;
        }
        if (microfrontState === 'fetching') {
            return;
        }
        window.addEventListener(
            HHPRO_RTE_LOAD_EVENT,
            () =>
                setTimeout(
                    dispatchClick,
                    // тут 1 потому что в текущей макротаске (timeout 0) нужно дать стейту примениться, и только после этой нулевой таски прокси компонент нормально словит событие после всех рендеров
                    1
                ),
            { once: true }
        );
        setMicrofrontState('fetching');
    }, [dispatchClick, microfrontState]);

    useEffect(() => {
        const handleChangeCount = (evt: CustomEvent<{ count: number }>) => {
            dispatch(setResumesTargetEmployerCount(evt.detail.count));
        };
        window.addEventListener(HHPRO_RTE_RESUMES_COUNT, handleChangeCount as EventListener);
        return () => {
            window.removeEventListener(HHPRO_RTE_RESUMES_COUNT, handleChangeCount as EventListener);
        };
    }, [dispatch]);

    useEffect(() => {
        const handleLoad = () => {
            setMicrofrontState('loaded');
        };
        if (microfrontState !== 'dirty') {
            window.addEventListener(HHPRO_RTE_LOAD_EVENT, handleLoad, { once: true });
        }
        return () => {
            window.removeEventListener(HHPRO_RTE_LOAD_EVENT, handleLoad);
        };
    }, [microfrontState]);

    const alreadyStyle = already ? 'primary' : 'secondary';

    return (
        <>
            <Button
                style="neutral"
                loading={microfrontState === 'fetching'}
                disabled={microfrontState === 'fetching'}
                onClick={handleButtonClick}
                stretched={stretched}
                size={isMenuItem ? 'medium' : 'small'}
                mode={isMenuItem ? 'tertiary' : alreadyStyle}
            >
                <div className={classnames(styles.buttonInner, { [styles.buttonInnerMenu]: isMenuItem })}>
                    <>{already ? trls[TrlKeys.already] : trls[TrlKeys.wantWorkHere]}</>
                    {showIcon &&
                        (isMenuItem ? (
                            <HhProSolidSize24 />
                        ) : (
                            <HhProSolidSize16 initialColor={already ? 'contrast' : 'primary'} />
                        ))}
                    {/* TODO magritte жду 3 версию кнопки чтобы сделать эти цифры https://jira.hh.ru/browse/PORTFOLIO-32245 */}
                    {!!already && !isMenuItem && showCounter && (
                        <Text style="secondary" typography="subtitle-2-semibold">
                            {countResumes}/{MAX_RESUMES}
                        </Text>
                    )}
                </div>
            </Button>
            {microfrontState !== 'dirty' && (
                <ContainerForMicroFrontend
                    place="applicantServicesResumeTargetEmployerManagement"
                    preloadContent={null}
                />
            )}
        </>
    );
};

export default translation(WantWorkHereManagementButton);
